/**
 * «📅 Календарь сроков ответа» — `#calendarBlock` (ui-blocks.md §5.3).
 *
 * Ровно 14 ячеек от сегодня плюс отдельная плитка «ПРОСРОЧЕНО».
 * Период не влияет: блок «на сейчас». Уровни заливки lv0…lv3 и признаки
 * `today` / `weekend` считает бэкенд.
 */

import type { KeyboardEvent } from 'react';
import { BLOCK_GAP, CAL_BODY, HEAD_NOTE_SM, fmtDate, fmtShort } from './format';
import type { CalendarBlock, OpenList, ShowToast } from './types';

interface Props {
    calendar: CalendarBlock;
    openList: OpenList;
    toast: ShowToast;
}

export function DeadlineCalendar({ calendar, openList, toast }: Props) {
    const openLate = (): void => {
        if (calendar.overdue <= 0) {
            toast('В этой выборке нет дел');
            return;
        }
        openList({ title: 'Просроченные дела', kpi: 'calendar_late' });
    };

    const openDay = (date: string, count: number): void => {
        if (count <= 0) {
            toast('В этой выборке нет дел');
            return;
        }
        openList({
            title: `Срок ответа ${fmtDate(date)}`,
            kpi: 'calendar_day',
            params: { date },
        });
    };

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>, run: () => void): void => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        run();
    };

    return (
        <div className="stat-block" style={BLOCK_GAP}>
            <div className="block-header">
                <span>📅 Календарь сроков ответа</span>
                <span style={HEAD_NOTE_SM}>ближайшие 14 дней</span>
            </div>
            <div className="block-body" style={CAL_BODY}>
                <div className="cal-wrap" id="calendarBlock">
                    <div
                        className={calendar.overdue > 0 ? 'cal-late' : 'cal-late empty'}
                        data-cal="late"
                        role="button"
                        tabIndex={0}
                        aria-label={`Просрочено: ${calendar.overdue}`}
                        onClick={openLate}
                        onKeyDown={(event) => onKeyDown(event, openLate)}
                    >
                        <div className="cal-day">ПРОСРОЧЕНО</div>
                        <div className="cal-num">{calendar.overdue}</div>
                        <div className="cal-dow">
                            {calendar.overdue > 0 ? 'требует ответа' : 'нарушений нет'}
                        </div>
                    </div>
                    <div className="cal-strip">
                        {calendar.cells.map((cell) => {
                            const classes = [`cal-cell`, `lv${cell.level}`];
                            if (cell.today) classes.push('today');
                            if (cell.weekend) classes.push('weekend');
                            return (
                                <div
                                    key={cell.date}
                                    className={classes.join(' ')}
                                    data-cal={cell.idx}
                                    role="button"
                                    tabIndex={0}
                                    aria-label={`${fmtDate(cell.date)}: ${cell.count}`}
                                    onClick={() => openDay(cell.date, cell.count)}
                                    onKeyDown={(event) =>
                                        onKeyDown(event, () => openDay(cell.date, cell.count))
                                    }
                                >
                                    <div className="cal-day">{fmtShort(cell.date)}</div>
                                    <div className="cal-num">{cell.count}</div>
                                    <div className="cal-dow">{cell.dow}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
